import React, { useState, useRef, useEffect, useMemo } from'react';
import { Calendar, ChevronDown, X } from'lucide-react';
import { format } from'date-fns';
import clsx from'clsx';

const RANGES = [
 { value:'5m', label:'5m' },
 { value:'15m', label:'15m' },
 { value:'1h', label:'1h' },
 { value:'6h', label:'6h' },
 { value:'24h', label:'24h' },
 { value:'7d', label:'7d' },
 { value:'30d', label:'30d' },
 { value:'90d', label:'90d' },
];

// Approx duration of each preset in ms — used to hide presets that fall
// outside an optional retention cap (see `maxDays`).
const RANGE_MS = {
 '5m': 5 * 60 * 1000,
 '15m': 15 * 60 * 1000,
 '1h': 60 * 60 * 1000,
 '6h': 6 * 60 * 60 * 1000,
 '24h': 24 * 60 * 60 * 1000,
 '7d': 7 * 24 * 60 * 60 * 1000,
 '30d': 30 * 24 * 60 * 60 * 1000,
 '90d': 90 * 24 * 60 * 60 * 1000,
};

// Quick-pick relative ranges inside the custom popover (pre-fill the inputs).
const QUICK_PICKS = [
 { label:'Last 1 hour', ms: 60 * 60 * 1000 },
 { label:'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
 { label:'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
 { label:'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
 { label:'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
];

// Azure Cost Management enforces a strict 1-year cap on its query window
// (returns BadRequest if exceeded), and the backend anchor that feeds
// custom ranges is sized just under that limit. Cap the picker at 364
// days so users can't request a window the backend would have to
// truncate.
const MAX_RANGE_MS = 364 * 24 * 60 * 60 * 1000; // backend hard limit

/**
 * Parse a`custom:<fromISO>:<toISO>` token. Returns`{ from, to }` Date pair
 * or`null` if the token is not a valid custom range.
 */
export function parseCustomRange(token) {
 if (typeof token !=='string' || !token.startsWith('custom:')) return null;
 const rest = token.slice('custom:'.length);
 const m = rest.match(/^(.+?Z?):(\d{4}-\d{2}-\d{2}T.+)$/);
 if (!m) return null;
 const from = new Date(m[1]);
 const to = new Date(m[2]);
 if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
 if (from >= to) return null;
 return { from, to };
}

/** Build a`custom:<fromISO>:<toISO>` token from two Date objects. */
export function buildCustomRange(from, to) {
 return`custom:${from.toISOString()}:${to.toISOString()}`;
}

/** Human-readable label for either a preset or custom-range token. */
export function formatTimeRangeLabel(value) {
 const c = parseCustomRange(value);
 if (c) {
 const sameDay = c.from.toDateString() === c.to.toDateString();
 if (sameDay) {
 return`${format(c.from,'MMM d, HH:mm')} – ${format(c.to,'HH:mm')}`;
 }
 return`${format(c.from,'MMM d, HH:mm')} – ${format(c.to,'MMM d, HH:mm')}`;
 }
 return value;
}

/**
 * Convert a Date to the`YYYY-MM-DDTHH:mm` shape expected by
 *`<input type="datetime-local">` (in the user's local time zone).
 */
function toLocalInputValue(date) {
 if (!date || Number.isNaN(date.getTime())) return'';
 const pad = (n) => String(n).padStart(2,'0');
 return (
`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
`T${pad(date.getHours())}:${pad(date.getMinutes())}`
 );
}

export default function TimeRangePicker({ value, onChange, maxDays }) {
 const isCustom = value?.startsWith('custom:');
 const customParsed = useMemo(() => parseCustomRange(value), [value]);

 // Optional retention cap (e.g. gateway logs are only kept ~45 days).
 // When set, we hide presets/quick-picks beyond the window and clamp the
 // custom range so users can't request data that no longer exists.
 const capMs = maxDays ? maxDays * 24 * 60 * 60 * 1000 : null;
 const effectiveMaxMs = capMs ? Math.min(capMs, MAX_RANGE_MS) : MAX_RANGE_MS;

 // Preset pills within the retention window.
 const ranges = useMemo(
 () => (capMs ? RANGES.filter((r) => (RANGE_MS[r.value] || 0) <= capMs) : RANGES),
 [capMs],
 );
 // Quick picks within the retention window.
 const quickPicks = useMemo(
 () => (capMs ? QUICK_PICKS.filter((q) => q.ms <= capMs) : QUICK_PICKS),
 [capMs],
 );
 // Earliest selectable instant for the custom "From" input.
 const minSelectable = useMemo(
 () => (capMs ? new Date(Date.now() - capMs) : null),
 [capMs],
 );

 const [open, setOpen] = useState(false);
 const [fromStr, setFromStr] = useState('');
 const [toStr, setToStr] = useState('');
 const [error, setError] = useState('');
 const popoverRef = useRef(null);
 const triggerRef = useRef(null);

 // When opening the popover, seed inputs from the current custom range
 // (if any), otherwise from a sensible default (last 24h).
 useEffect(() => {
 if (!open) return;
 if (customParsed) {
 setFromStr(toLocalInputValue(customParsed.from));
 setToStr(toLocalInputValue(customParsed.to));
 } else {
 const now = new Date();
 const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
 setFromStr(toLocalInputValue(yesterday));
 setToStr(toLocalInputValue(now));
 }
 setError('');
 }, [open, customParsed]);

 // Close popover on outside click / escape.
 useEffect(() => {
 if (!open) return;
 const onDocClick = (e) => {
 if (
 popoverRef.current && !popoverRef.current.contains(e.target) &&
 triggerRef.current && !triggerRef.current.contains(e.target)
 ) {
 setOpen(false);
 }
 };
 const onKey = (e) => {
 if (e.key ==='Escape') setOpen(false);
 };
 document.addEventListener('mousedown', onDocClick);
 document.addEventListener('keydown', onKey);
 return () => {
 document.removeEventListener('mousedown', onDocClick);
 document.removeEventListener('keydown', onKey);
 };
 }, [open]);

 const applyQuickPick = (ms) => {
 const now = new Date();
 const from = new Date(now.getTime() - ms);
 setFromStr(toLocalInputValue(from));
 setToStr(toLocalInputValue(now));
 setError('');
 };

 const apply = () => {
 const from = fromStr ? new Date(fromStr) : null;
 const to = toStr ? new Date(toStr) : null;
 if (!from || Number.isNaN(from.getTime())) {
 setError('Pick a valid start date and time.');
 return;
 }
 if (!to || Number.isNaN(to.getTime())) {
 setError('Pick a valid end date and time.');
 return;
 }
 if (from >= to) {
 setError('Start must be before end.');
 return;
 }
 if (to - from > effectiveMaxMs) {
 setError(
 capMs
 ? `Gateway logs are only retained ${maxDays} days — pick a shorter range.`
 : 'Range cannot exceed 365 days.',
 );
 return;
 }
 if (minSelectable && from < minSelectable) {
 setError(`Start can't be earlier than ${maxDays} days ago (log retention).`);
 return;
 }
 if (to > new Date(Date.now() + 60 * 1000)) {
 setError('End cannot be in the future.');
 return;
 }
 onChange(buildCustomRange(from, to));
 setOpen(false);
 };

 return (
 <div className="flex items-center gap-1.5 shrink-0">
 {/* Preset pill group */}
 <div className="flex items-center bg-gray-100 rounded-lg p-0.5 shrink-0">
 {ranges.map(({ value: v, label }) => (
 <button
 key={v}
 onClick={() => onChange(v)}
 className={clsx(
'px-2.5 py-1 text-xs font-medium rounded-md transition-all shrink-0',
 v === value
 ?'bg-white text-brand-700 shadow-sm'
 :'text-gray-500 hover:text-gray-700'
 )}
 >
 {label}
 </button>
 ))}
 </div>

 {/* Custom range trigger + popover */}
 <div className="relative shrink-0">
 <button
 ref={triggerRef}
 type="button"
 onClick={() => setOpen((o) => !o)}
 className={clsx(
'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all',
 isCustom
 ?'bg-brand-50 border-brand-300 text-brand-700'
 :'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
 )}
 title="Custom date range"
 aria-haspopup="dialog"
 aria-expanded={open}
 >
 <Calendar className="w-3.5 h-3.5" />
 <span className="max-w-[180px] truncate">
 {isCustom ? formatTimeRangeLabel(value) :'Custom'}
 </span>
 <ChevronDown
 className={clsx(
'w-3 h-3 transition-transform',
 open &&'rotate-180'
 )}
 />
 </button>

 {open && (
 <div
 ref={popoverRef}
 role="dialog"
 aria-label="Select custom date range"          className="absolute right-0 mt-2 w-[calc(100vw-1.5rem)] sm:w-[340px] max-w-[340px] z-50 bg-white border border-gray-200 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden"
 >
 <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
 <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
 <Calendar className="w-4 h-4 text-brand-600" />
 Custom range
 </div>
 <button
 onClick={() => setOpen(false)}
 className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
 aria-label="Close"
 >
 <X className="w-3.5 h-3.5" />
 </button>
 </div>

 <div className="p-4 space-y-3">
 {/* Quick picks */}
 <div>
 <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-1.5">
 Quick picks
 </div>
 <div className="flex flex-wrap gap-1.5">
 {quickPicks.map((q) => (
 <button
 key={q.label}
 onClick={() => applyQuickPick(q.ms)}
 className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700 hover:bg-brand-50 hover:text-brand-700 transition-colors"
 >
 {q.label}
 </button>
 ))}
 </div>
 </div>

 {/* Date inputs */}
 <div className="grid grid-cols-1 gap-2.5">
 <label className="block">
 <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-400">
 From
 </span>
 <input
 type="datetime-local"
 value={fromStr}
 onChange={(e) => { setFromStr(e.target.value); setError(''); }}
 max={toStr || undefined}
 min={minSelectable ? toLocalInputValue(minSelectable) : undefined}
 className="mt-1 w-full text-sm rounded-md border border-gray-300 bg-white text-gray-800 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
 />
 </label>
 <label className="block">
 <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-400">
 To
 </span>
 <input
 type="datetime-local"
 value={toStr}
 onChange={(e) => { setToStr(e.target.value); setError(''); }}
 min={fromStr || undefined}
 className="mt-1 w-full text-sm rounded-md border border-gray-300 bg-white text-gray-800 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
 />
 </label>
 </div>

 {error && (
 <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5">
 {error}
 </div>
 )}

 {capMs && !error && (
 <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
 Gateway logs are retained for {maxDays} days. Ranges are limited to
 this window.
 </div>
 )}
 </div>

 <div className="flex items-center justify-end gap-2 px-4 py-2.5 bg-gray-50 border-t border-gray-100">
 <button
 onClick={() => setOpen(false)}
 className="px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100"
 >
 Cancel
 </button>
 <button
 onClick={apply}
 className="px-3 py-1.5 text-xs font-semibold rounded-md bg-brand-600 hover:bg-brand-700 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
 >
 Apply
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 );
}
